import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import useToast from "../../../util/useToast";
import {editorConfigSelector, rxHideAccountEditor, rxSubmitAccountEditor, selectAllAccounts} from "../../../slices/accountsSlice";
import {useEffect, useState} from "react";
import {MODE_EDIT, NEGATIVE_AMOUNT_REGEX} from "../../../codes";
import {createAccount, updateAccount} from "../../../api/ApiAccounts";
import {DEFAULT_COLOR} from "../../../common/ColorBox";
import {getOrDefault} from "../../../util/util";
import useNetwork from "../../../util/useNetwork";
import {appConfigSelector} from "../../../slices/appSlice";
import useValidator from "../../../util/useValidator";

const useAccountEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("transactions");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const {currencyMain} = useSelector(appConfigSelector);
  const {show, mode, account} = useSelector(editorConfigSelector);
  const accounts = useSelector(selectAllAccounts);

  const [editedAccount, setEditedAccount] = useState({errors: {}, name: "", description: "", balance: ""});

  const hideAccountEditor = () => dispatch(rxHideAccountEditor());

  useEffect(() => {
    if (!account || !show) {
      return;
    }

    setEditedAccount({
      errors: {},
      name: getOrDefault(account.name, ""),
      description: getOrDefault(account.description, ""),
      currency: getOrDefault(account.currency, currencyMain),
      balance: "" + getOrDefault(account.balance, ""),
      active: getOrDefault(account.active, true),
      color: getOrDefault(account.color, DEFAULT_COLOR),
      icon: getOrDefault(account.icon, "")
    });
  }, [show, account, currencyMain]);

  const getErrors = values => {
    const errors = {};

    if (!values.name.trim().length) {
      errors.name = "account_editor_error_name_must_be_filled";
    } else if (accounts.find(a => a !== account && a.name.toLowerCase() === values.name.trim().toLowerCase())) {
      errors.name = "account_editor_error_name_already_taken";
    }

    if (values.balance === undefined) {
      errors.balance = "account_editor_error_balance_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    let balance;

    if (editedAccount.balance.length) {
      balance = +editedAccount.balance.replace(",", ".");
    }

    const values = {
      name: editedAccount.name,
      description: editedAccount.description,
      currency: editedAccount.currency,
      balance,
      active: editedAccount.active,
      color: editedAccount.color,
      icon: editedAccount.icon
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateAccount(account.id, values);
        } else {
          return createAccount(values);
        }
      })
      .then(result => dispatch(rxSubmitAccountEditor(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "account_edited" : "account_added"));

        hideAccountEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedAccount(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedAccount = field => event => {
    const values = {};

    switch (field) {
      case "balance": {
        const balance = event.target.value;

        if (!NEGATIVE_AMOUNT_REGEX.test(balance)) {
          return;
        }

        values.balance = balance;

        break;
      }
      case "color":
      case "currency":
      case "active":
        values[field] = event;
        break;
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedAccount(prev => ({...prev, ...values}));
  };

  return {
    show,
    t,
    mode,
    hideAccountEditor,
    account,
    editedAccount,
    submit,
    updateEditedAccount
  };
};

export default useAccountEditor;
